import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, TrendingUp, Key, Home, ArrowRight, Star, ShieldCheck, Clock } from 'lucide-react';
import ChatbotContact from '../components/ChatbotContact';

// --- COMPONENTE PER ANIMAZIONI SCROLL ---
const RevealOnScroll = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        if (ref.current) observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={`transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
        >
            {children}
        </div>
    );
};

const OwnersContent = () => {
    const navigate = useNavigate();
    const BRAND_CORAL = "#E84C5B";

    // Data for Quick Benefits (The Pyramid)
    const benefits = [
        {
            icon: <TrendingUp className="w-6 h-6 md:w-8 md:h-8" style={{ color: BRAND_CORAL }} />,
            title: "Maximize Earnings",
            desc: "Dynamic pricing algorithms tailored to demand."
        },
        {
            icon: <ShieldCheck className="w-6 h-6 md:w-8 md:h-8" style={{ color: BRAND_CORAL }} />,
            title: "Complete Care",
            desc: "Professional cleaning and rigorous inspections."
        },
        {
            icon: <Clock className="w-6 h-6 md:w-8 md:h-8" style={{ color: BRAND_CORAL }} />,
            title: "24/7 Management",
            desc: "We handle guests so you don't have to."
        }
    ];

    const features = [
        {
            icon: <Camera className="w-8 h-8 text-[#E84C5B]" />,
            title: "Discreet, High-Impact Marketing",
            subtitle: "Positioned for the Right Guests — Not Just More Guests",
            text: "We curate each Hamptons residence as a refined hospitality offering, crafted to appeal to a discerning audience. Our marketing includes editorial-grade photography, cinematic video, and detailed floor plans—designed to highlight architectural quality, location, and lifestyle. Your home is selectively positioned on premium short-term rental platforms including Airbnb and VRBO."
        },
        {
            icon: <TrendingUp className="w-8 h-8 text-[#E84C5B]" />,
            title: "A Smarter Way to Host—Without Compromise",
            subtitle: "Thoughtful booking support and hospitality care, designed around your home.",
            text: "We approach each home as a unique hospitality experience, shaped by the Hamptons season, your personal use, and the standards your property deserves. Every stay is handled with intention and discretion, prioritizing guest quality, operational excellence, and long‑term care—so your home performs thoughtfully, never at the expense of its integrity."
        },
        {
            icon: <Key className="w-8 h-8 text-[#E84C5B]" />,
            title: "White-Glove Hospitality Operations",
            subtitle: "Hands-Off Ownership, Total Confidence",
            text: "From arrival to departure, every stay is professionally overseen. We conduct detailed pre-stay and post-stay inspections, coordinate luxury-standard housekeeping and laundry, and manage guest communication with discretion and responsiveness. Any damages are promptly documented and handled through structured claims processes."
        },
        {
            icon: <Home className="w-8 h-8 text-[#E84C5B]" />,
            title: "Year-Round Property Stewardship",
            subtitle: "Protecting Your Hamptons Home Beyond the Season",
            text: "Your home is monitored year-round through scheduled inspections and comprehensive reporting. Seasonal maintenance and preventative care are planned and executed with precision. We manage inventories, restock essentials, and ensure the property is always guest-ready—or owner-ready—at a moment’s notice."
        }
    ];

    return (
        <div className="font-sans w-full overflow-x-hidden bg-[#F6F4F1]">
            <ChatbotContact />
            {/* --- HEADER SECTION --- */}
            <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-6 max-w-5xl mx-auto text-center">
                <RevealOnScroll>
                    <span className="text-[#E84C5B] font-bold tracking-[0.2em] uppercase text-xs md:text-sm mb-4 block">
                        Another Home
                    </span>
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium text-[#1F2A33] mb-6 leading-tight">
                        Where Refined Hospitality Meets Exceptional Hamptons Homes
                    </h1>
                </RevealOnScroll>
            </section>

            {/* --- HERO IMAGE --- */}
            <div className="w-full px-4 md:px-8 max-w-[1400px] mx-auto mb-16 md:mb-24">
                <RevealOnScroll delay={200}>
                    <div className="relative aspect-[16/9] md:aspect-[21/9] w-full rounded-[2rem] overflow-hidden shadow-2xl group">
                        <img
                            src="/img/Owner.jpg"
                            alt="Hamptons Luxury Home"
                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A33]/40 to-transparent"></div>
                    </div>
                </RevealOnScroll>
            </div>

            {/* --- INTRO MANIFESTO --- */}
            <section className="px-6 max-w-4xl mx-auto mb-20 md:mb-28 text-center md:text-left">
                <RevealOnScroll>
                    <div className="border-l-4 border-[#E84C5B] pl-6 md:pl-10 py-2">
                        <h2 className="text-2xl md:text-4xl font-serif text-[#1F2A33] leading-relaxed mb-6">
                            "Your Hamptons home is more than a seasonal retreat. It is a private asset that deserves thoughtful positioning, careful oversight, and a level of service aligned with its value."
                        </h2>
                        <p className="text-lg md:text-xl text-[#1F2A33]/80 leading-relaxed font-light">
                            At <strong className="text-[#1F2A33] font-medium">Another Home</strong>, we elevate luxury second homes through a discreet, performance‑driven approach rooted in care, privacy, and long‑term integrity.
                        </p>
                    </div>
                </RevealOnScroll>
            </section>

            {/* --- QUICK BENEFITS (THE PYRAMID LAYOUT) --- */}
            <section className="px-6 max-w-7xl mx-auto mb-24 md:mb-32">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {/* Card 1 */}
                    <div className="bg-white p-8 rounded-[2rem] hover:shadow-xl transition-all duration-300 border border-[#D6D1CB] group">
                        <div className="w-16 h-16 bg-[#E84C5B]/10 rounded-2xl flex items-center justify-center text-[#E84C5B] mb-6 group-hover:scale-110 transition-transform">
                            <TrendingUp className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-[#1F2A33] mb-4 font-serif">Enhance Booking Performance</h3>
                        <p className="text-sm md:text-base text-[#1F2A33]/70 leading-relaxed">
                            We use market insights, booking‑channel data, and availability trends to support informed owner decisions and improve booking performance across seasons—while owners retain full control over pricing and booking terms.                        </p>
                    </div>

                    {/* Card 2 */}
                    <div className="bg-white p-8 rounded-[2rem] hover:shadow-xl transition-all duration-300 border border-[#D6D1CB] group">
                        <div className="w-16 h-16 bg-[#E84C5B]/10 rounded-2xl flex items-center justify-center text-[#E84C5B] mb-6 group-hover:scale-110 transition-transform">
                            <ShieldCheck className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-[#1F2A33] mb-4 font-serif">Complete Care</h3>
                        <p className="text-sm md:text-base text-[#1F2A33]/70 leading-relaxed">
                            From professional cleaning to routine maintenance, we handle everything. Your property is inspected after every stay.
                        </p>
                    </div>

                    {/* Card 3 */}
                    <div className="bg-white p-8 rounded-[2rem] hover:shadow-xl transition-all duration-300 border border-[#D6D1CB] group">
                        <div className="w-16 h-16 bg-[#E84C5B]/10 rounded-2xl flex items-center justify-center text-[#E84C5B] mb-6 group-hover:scale-110 transition-transform">
                            <Clock className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-[#1F2A33] mb-4 font-serif">24/7 Management</h3>
                        <p className="text-sm md:text-base text-[#1F2A33]/70 leading-relaxed">
                            Forget about late-night calls. Our team handles all guest communication, check-ins, and emergencies around the clock.
                        </p>
                    </div>
                </div>
            </section>

            {/* --- DETAILED PILLARS --- */}
            <section className="bg-white py-20 md:py-32 rounded-t-[3rem] md:rounded-t-[5rem] shadow-[0_-20px_40px_-15px_rgba(0,0,0,0.05)] overflow-hidden">
                <div className="max-w-7xl mx-auto px-6">
                    <RevealOnScroll>
                        <div className="text-center mb-16">
                            <span className="text-[#E84C5B] font-bold tracking-widest uppercase text-xs">Our Approach</span>
                            <h2 className="text-3xl md:text-5xl font-serif text-[#1F2A33] mt-3">The Four Pillars</h2>
                        </div>
                    </RevealOnScroll>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                        {features.map((item, index) => (
                            <div key={index}>
                                <RevealOnScroll delay={index * 100}>
                                    <div className="relative group bg-[#F6F4F1] rounded-[2.5rem] p-8 md:p-12 overflow-hidden hover:-translate-y-2 hover:shadow-xl transition-all duration-500 ease-out h-full">

                                        {/* Big Number Background */}
                                        <div className="absolute -right-4 -bottom-10 text-[10rem] md:text-[12rem] font-serif font-bold text-[#1F2A33]/5 select-none pointer-events-none transition-transform duration-500 group-hover:scale-110 group-hover:text-[#E84C5B]/5 group-hover:rotate-12">
                                            0{index + 1}
                                        </div>

                                        {/* Content */}
                                        <div className="relative z-10 flex flex-col h-full">
                                            <div className="self-start p-4 rounded-full mb-8 shadow-sm group-hover:shadow-md transition-shadow" style={{ backgroundColor: `${BRAND_CORAL}1A` }}>
                                                {item.icon}
                                            </div>

                                            <h3 className="text-2xl md:text-3xl font-serif text-[#1F2A33] mb-2">
                                                {item.title}
                                            </h3>
                                            <p className="text-[#E84C5B] font-bold text-xs tracking-wider uppercase mb-6">
                                                {item.subtitle}
                                            </p>

                                            <p className="text-[#1F2A33]/70 text-base leading-relaxed max-w-md">
                                                {item.text}
                                            </p>
                                        </div>
                                    </div>
                                </RevealOnScroll>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- CTA BANNER (CONTACT PART - INVERTED PYRAMID) --- */}
            <section className="bg-[#1F2A33] text-[#F6F4F1] py-20 md:py-32 px-6 relative overflow-hidden">
                {/* Decorative circles animated */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#E84C5B] rounded-full blur-[100px] opacity-20 translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-[100px] opacity-10 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10 flex flex-col items-center">
                    <RevealOnScroll>
                        {/* 1. Badge (Smallest) */}
                        <div className="inline-flex items-center justify-center p-3 mb-8 rounded-full border border-[#E84C5B]/30 bg-[#E84C5B]/10 backdrop-blur-sm">
                            <Star className="w-5 h-5 text-[#E84C5B] mr-2" fill="#E84C5B" />
                            <span className="text-[#E84C5B] font-bold text-sm tracking-wide uppercase">Private Consultation</span>
                        </div>

                        {/* 2. Headline (Widest)
                        <h2 className="text-4xl md:text-6xl font-serif mb-6 leading-tight max-w-3xl">
                            Ready to elevate your <br className="hidden md:block" />
                            Hamptons experience?

                        </h2>*/}
                        <h2 className="text-4xl md:text-6xl font-serif mb-6 leading-tight text-center">
                            <span className="block">
                                Ready to elevate your
                            </span>
                            <span className="block text-[0.9em] tracking-tight">
                                Hamptons experience?
                            </span>
                        </h2>


                        {/* 3. Subheadline (Medium) */}
                        <p className="text-lg md:text-xl text-[#D6D1CB] mb-10 font-light max-w-xl mx-auto leading-relaxed">
                            Let's discuss a bespoke hospitality strategy for your property. We invite you to schedule <br />a private conversation.
                        </p>

                        {/* 4. Button (Narrow) */}
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <button
                                onClick={() => navigate('/contact')}
                                className="bg-[#E84C5B] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#1F2A33] transition-all duration-300 shadow-xl flex items-center justify-center gap-3 active:scale-95 transform hover:-translate-y-1"
                            >
                                Contact Us <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </RevealOnScroll>
                </div>
            </section>

        </div>
    );
};

export default OwnersContent;